// API endpoints for file uploads
import { api, backendFetch } from './client';
import { supabase } from './supabase-client';

export interface UploadImageResponse {
  url: string;
  filename: string;
}

export interface UploadVideoResponse {
  url: string;
  filename: string;
}

export const uploadApi = {
  /**
   * Загрузить изображение и получить публичный URL
   */
  uploadImage: async (file: File): Promise<UploadImageResponse> => {
    // Для маленьких файлов (< 1MB) используем base64, для больших - FormData
    if (file.size < 1024 * 1024) {
      // Конвертируем файл в base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      return api.post<UploadImageResponse>('/upload/image', {
        file: base64,
        filename: file.name,
      });
    } else {
      // Используем FormData для больших файлов
      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', file.name);
      
      return api.postFormData<UploadImageResponse>('/upload/image-formdata', formData);
    }
  },

  /**
   * Загрузить видео и получить публичный URL
   */
  uploadVideo: async (file: File): Promise<UploadVideoResponse> => {
    // Используем FormData для загрузки больших файлов (видео)
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);
    
    return api.postFormData<UploadVideoResponse>('/upload/video', formData);
  },

  /**
   * Загрузить видео для рилса напрямую в Supabase Storage через XHR (с поддержкой onProgress).
   * Использует createSignedUploadUrl + XHR PUT для получения реальных событий onprogress.
   * Фаза загрузки занимает 0–50% прогресса.
   */
  uploadReel: async (
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<UploadVideoResponse> => {
    const bucketName = 'make-bde4f709-reels';
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(7);
    const ext = file.name.split('.').pop() || 'mp4';
    const filename = `${timestamp}-${randomString}.${ext}`;

    console.log('🚀 Начинаем прямую загрузку видео в Storage:', {
      originalName: file.name,
      filename,
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      type: file.type,
    });

    const { data: signedData, error: signedError } = await supabase.storage
      .from(bucketName)
      .createSignedUploadUrl(filename);

    if (signedError || !signedData) {
      throw new Error(`Не удалось получить URL загрузки: ${signedError?.message}`);
    }

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress?.(Math.round((e.loaded / e.total) * 50));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      };
      xhr.onerror = () => reject(new Error('Upload network error'));
      xhr.open('PUT', signedData.signedUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
      xhr.send(file);
    });

    console.log('✅ Файл загружен в Storage:', filename);

    const { data: publicUrlData } = supabase.storage
      .from(bucketName)
      .getPublicUrl(filename);

    if (!publicUrlData?.publicUrl) {
      throw new Error('Не удалось получить публичный URL для видео');
    }

    console.log('✅ Видео загружено с публичным URL:', { filename, url: publicUrlData.publicUrl });

    return { url: publicUrlData.publicUrl, filename };
  },

  /**
   * Принудительно ремуксит видео в MP4 контейнер (без перекодирования) на Vercel-функции.
   * Возвращает новый publicUrl. Старый файл остаётся в Storage.
   * Анимирует прогресс 50→89% через таймер, затем вызывает onProgress(90) в finally.
   */
  remuxToMp4: async (
    videoUrl: string,
    originalFilename?: string,
    onProgress?: (pct: number) => void,
  ): Promise<UploadVideoResponse> => {
    const REMUX_ESTIMATE_MS = 60_000;
    const start = Date.now();
    const timer = setInterval(() => {
      const pct = Math.min(89, 50 + Math.round(((Date.now() - start) / REMUX_ESTIMATE_MS) * 40));
      onProgress?.(pct);
    }, 500);

    try {
      const response = await backendFetch('/api/remux-reel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl, originalFilename }),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `Remux failed (${response.status})`);
      }
      clearInterval(timer);
      onProgress?.(90);
      return response.json();
    } catch (err) {
      clearInterval(timer);
      throw err;
    }
  },
};

export type UploadProgressCallback = (pct: number) => void;

/**
 * Returns true when a user-uploaded file likely needs remux.
 * MOV/HEVC/WebM — definitely yes. MP4 is passed through; Instagram will
 * error if it turns out to be incompatible (e.g. iPhone VFR + High profile).
 */
export function needsRemux(file: File): boolean {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return (
    type === 'video/quicktime' ||
    type.startsWith('video/webm') ||
    type.includes('hevc') ||
    name.endsWith('.mov') ||
    name.endsWith('.hevc')
  );
}

/**
 * Returns true when a processed blob produced by VideoProcessor (MediaRecorder) needs
 * server-side remux. Reliable because MediaRecorder sets type explicitly.
 */
export function processedBlobNeedsRemux(blob: Blob): boolean {
  return blob.type.startsWith('video/webm');
}

/**
 * Загружает видео и **всегда** прогоняет через бэковый транскод в H.264 Baseline
 * 30fps CFR. iPhone пишет MP4 с VFR и High profile, которые Reels API отвергает
 * так же, как и MOV — поэтому проверка по расширению/MIME-типу не спасает.
 *
 * onProgress: 0–50% — XHR upload, 50–89% — таймер remux, 90% — remux завершён.
 */
export async function uploadReelEnsuringMp4(
  file: File,
  onProgress?: UploadProgressCallback,
): Promise<UploadVideoResponse> {
  const uploaded = await uploadApi.uploadReel(file, onProgress);
  console.log('🔁 Запускаем нормализацию видео на бэке:', { type: file.type, name: file.name });
  const remuxed = await uploadApi.remuxToMp4(uploaded.url, file.name, onProgress);
  console.log('✅ Нормализация завершена:', remuxed);
  return remuxed;
}